// Voice-input transcription (VOXTRAL_TRANSCRIPTION_HANDOFF_2026-07-01):
// POST {billing api base}/v1/audio/transcriptions with a RAW binary audio
// body — audio/wav or audio/mpeg only (the backend rejects WebM/Opus and
// multipart). The transcript is inserted into the composer for the user to
// review and send; it is NEVER auto-submitted.
import { requireBillingOpenAnalystApiAccessToken } from "@/services/billing-auth"
import { openAnalystBillingApiBaseUrl } from "@/utils/openanalyst-hosts"

const API_BASE = openAnalystBillingApiBaseUrl()

let configuredFetch: typeof fetch | null = null

export function configureTranscriptionFetch(fetcher: typeof fetch | null) {
  configuredFetch = fetcher
}

export type TranscriptionResult = {
  transcript: string
  credits?: number
}

// Friendly mappings per handoff §15.8 — the backend's typed errors are
// accurate but not user-facing copy.
const FRIENDLY_BY_STATUS: Record<number, string> = {
  401: "Sign in again to use voice input.",
  403: "Voice input isn't enabled for this account yet.",
  413: "That recording is too long or too large. Try a shorter one.",
  415: "That audio format isn't supported. Please try again.",
  502: "The transcription service had a problem. Please retry.",
  504: "Transcription timed out. Try a shorter recording.",
}

export async function transcribeAudio(blob: Blob, mime: string): Promise<TranscriptionResult> {
  const response = await (configuredFetch ?? fetch)(`${API_BASE}/v1/audio/transcriptions`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${await requireBillingOpenAnalystApiAccessToken()}`,
      "Content-Type": mime,
    },
    body: blob,
  })

  if (!response.ok) {
    const detail = (await response.json().catch(() => undefined)) as
      | { error?: { type?: string; message?: string } }
      | undefined
    throw new Error(
      FRIENDLY_BY_STATUS[response.status] ??
        detail?.error?.message ??
        `Transcription failed (HTTP ${response.status}).`,
    )
  }

  const data = (await response.json().catch(() => undefined)) as
    | { transcript?: string; usage?: { credits?: number } }
    | undefined
  const transcript = data?.transcript?.trim()
  if (!transcript) throw new Error("No speech detected. Try again, closer to the microphone.")
  return { transcript, credits: data?.usage?.credits }
}
